// tslint:disable: max-line-length
import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showErrorMessage } from '../../helpers/showMessage';
import { loginUser } from '../../requests/login';
import { setItem } from '../../utils/localStorage';
import { confirmAlert } from 'react-confirm-alert';
import Icon from 'lqa-ui/lib/Components/Icon';
import { SUPPORT_URL } from '../../utils/constants';
import { handleBrandAndHotelData } from '../../helpers/handleBrandAndHotelData';
import { trackSessionStart } from '../../utils/myLqaAnalytics';
import { ClearAllIndexedDB } from '../../indexed';
import { ButtonLarge } from '../../app/common/sharedComponents/buttons/ButtonLarge';
import { TextInput } from '../../app/common/sharedComponents/TextInput';
import { useTranslation } from 'react-i18next';
import {
  PhoneNumberContainer,
  ForgotPasswordWrapper,
  FormContainer,
  LoginButtonContainer,
  LoginContainer,
} from './styled.registerSuccess';
import { StyledTextLabel } from '../../app/common/sharedComponents/TextInput/styled.textInput';
import { Body5 } from '../../app/common/typography';
import EndSessionModal from '../commons/EndSessionModal';
import { LevelTypes } from '../../enums/Level';
import { PhoneContainer } from './../../app/common/sharedStyledComponents/PhoneInput.styled';

const LoginForm = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const isRedirectedFromParams = params.get('isRegistered');
  const isRedirectedFromRegisterScreen =
    isRedirectedFromParams && JSON.parse(isRedirectedFromParams);
  setItem('hotelList', []);
  const userEmail = localStorage.getItem('email');
  const userPhone = localStorage.getItem('phonenumber');
  const [inputValues, setInputValues] = useState({
    username: userEmail ? userEmail.trim().toLowerCase() : '',
    userphone: userPhone
      ? String(userPhone)
          .replace(/[()+-]/g, '')
          .replace(/\s/g, '')
      : '',
    password: '',
  });

  const [loginErrorState, setLoginErrorState] = useState(false);
  const [validatingLogin, setValidatingLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMultipleSessionFound, setIsMultipleSessionFound] = useState(false);

  const handleForgotPassword = () => {
    navigate('/forgotpassword');
  };

  const inActiveUserNotification = () =>
    confirmAlert({
      overlayClassName: 'overlayPrompt',
      customUI: ({ onClose }) => {
        return (
          <div className="backOverlay">
            <span className="d-block  text-right mb-20 " onClick={onClose}>
              <Icon kind="close" height={17} width={17} />
            </span>
            <p className="sans-font font-normal fw-400 f-15 lh-20 text-center color-black">
              Your account has been deactivated. If you set up a myLQA
              membership, you may log in with those credentials. If you feel you
              were deactivated in error, please get in touch with your Hotel
              administrator
            </p>
          </div>
        );
      },
    });

  const setShowAccountNotFoundModal = (message) =>
    confirmAlert({
      overlayClassName: 'overlayPrompt',
      customUI: ({ onClose }) => {
        return (
          <div className="backOverlay">
            <span className="d-block  text-right " onClick={onClose}>
              <Icon kind="close" height={17} width={17} />
            </span>
            <h1 className="sans-font font-normal fw-600 f-20 lh-28 text-center color-black">
              {message === 'noSubscription'
                ? 'No Active Subscription'
                : 'myLQA Account Not Found'}{' '}
            </h1>
            <p className="sans-font font-normal fw-400 f-15 lh-20 text-center color-black">
              It seems that you don't have access to the myLQA app with your
              current user profile. Contact your system administrator to set up
              or activate your account. For further assistance, reach out to{' '}
              <a className="support-link">{SUPPORT_URL}</a>
            </p>
          </div>
        );
      },
    });

  const signInHandler = async (event, isLogoutOther) => {
    ClearAllIndexedDB();
    if (
      inputValues.password === '' ||
      (!inputValues.username && !inputValues.userphone) ||
      (inputValues.username && inputValues.userphone) ||
      (inputValues.userphone && inputValues.userphone.length < 10)
    ) {
      showErrorMessage('Please enter a valid login details');
      return;
    }
    event.preventDefault();
    setValidatingLogin(true);
    setLoading(true);
    try {
      const authResponse: any = await loginUser(
        inputValues.username,
        inputValues.userphone,
        inputValues.password,
        isLogoutOther
      );
      setItem('user', JSON.stringify(authResponse.data));
      setItem('userProfile', JSON.stringify(authResponse.data.userProfile));
      // Store the raw string (no JSON.stringify) so getItem round-trips it unchanged.
      setItem('sessionId', authResponse.data.sessionId);
      if (inputValues.userphone) {
        setItem('login-phone', inputValues.userphone);
      } else if (inputValues.username) {
        setItem('login-email', inputValues.username.trim().toLowerCase());
      }
      const userData = authResponse.data;
      if (
        userData &&
        userData.user &&
        userData.user.brands &&
        userData.user.brands.length
      ) {
        handleBrandAndHotelData(userData.user.brands);
      }
      if (userData && userData.accessToken) {
        trackSessionStart();
      }
      setLoading(false);
      setTimeout(() => {
        const userId = userData && userData.user ? userData.user.id : '';
        const level = userData && userData.user && userData.user.level;
        const lowerLevels: LevelTypes[] = ['staff', 'teamLead'];
        const isIosApp: boolean = navigator.userAgent.includes('iPhone');
        if (lowerLevels.includes(level)) {
          window.location.href = `/myactions?userId=${isIosApp ? userId : ''}`;
        } else {
          window.location.href = `/dashboard?userId=${isIosApp ? userId : ''}`;
        }
      }, 1000);
    } catch (err: any) {
      setLoading(false);
      if (err && err.response && err.response.data) {
        const { statusCode, message } = err.response.data;
        switch (statusCode) {
          case 405:
            inActiveUserNotification();
            break;
          case 403:
            setShowAccountNotFoundModal(message);
            break;
          case 409:
            setIsMultipleSessionFound(true);
            break;
          default:
            setLoginErrorState(true);
            setValidatingLogin(false);
            break;
        }
      } else {
        showErrorMessage('Something went wrong while login');
      }
    }
  };

  const handleChange = (name: string, value: string) => {
    const lowercasedValue = name === 'username' ? value.toLowerCase() : value;

    setInputValues({ ...inputValues, [name]: lowercasedValue });
  };

  const handleOnChangePhone = (value: string, data: any): any => {
    if (value.slice(data.dialCode.length)) {
      setInputValues({ ...inputValues, userphone: value });
    } else {
      setInputValues({
        ...inputValues,
        userphone: value.slice(data.dialCode.length),
      });
    }
  };

  return (
    <LoginContainer>
      <FormContainer>
        <TextInput
          title={t('COMMON.REGISTER.LABELS.YOUR_EMAIL')}
          placeholder={t('COMMON.REGISTER.LABELS.TYPE_YOUR_EMAIL')}
          textValue={inputValues.username}
          onValueChange={(value: string) => handleChange('username', value)}
          disabled={validatingLogin || !!inputValues.userphone}
          inputType="email"
        />
        <PhoneNumberContainer>
          <StyledTextLabel>
            <Body5 color="eerieBlack">
              {t('COMMON.REGISTER.LABELS.MOBILE_PHONE_NUMBER')}
            </Body5>
          </StyledTextLabel>
          <PhoneContainer>
            <PhoneInput
              country={'us'}
              value={inputValues.userphone}
              placeholder=""
              dropdownClass={'phone-input-dropdown'}
              containerClass={'phone-input-dropdown'}
              countryCodeEditable={false}
              onChange={handleOnChangePhone}
              disabled={validatingLogin || !!inputValues.username}
            />
          </PhoneContainer>
        </PhoneNumberContainer>
        <TextInput
          title={t('COMMON.REGISTER.LABELS.YOUR_PASSWORD')}
          placeholder={t('COMMON.REGISTER.LABELS.PASSWORD')}
          textValue={inputValues.password}
          onValueChange={(value: string) => handleChange('password', value)}
          inputType="password"
        />
        {loginErrorState && (
          <Body5 color="redCrayola">
            {`${t('COMMON.OUTSIDE.MESSAGES.DOES_NOT_MATCH_OUR_RECORDS_REVIEW_OR')} `}
            <ForgotPasswordWrapper
              color="redCrayola"
              onClick={handleForgotPassword}
            >
              {t('COMMON.OUTSIDE.BUTTONS.FORGOT_PASSWORD')}
            </ForgotPasswordWrapper>
          </Body5>
        )}
        <LoginButtonContainer>
          <ButtonLarge
            onClick={(event) => signInHandler(event, false)}
            loading={loading}
            title={
              isRedirectedFromRegisterScreen
                ? t('COMMON.OUTSIDE.BUTTONS.LOGIN')
                : t('COMMON.REGISTER.BUTTONS.START_USING_MYLQA')
            }
          />
        </LoginButtonContainer>
      </FormContainer>
      {isMultipleSessionFound && (
        <EndSessionModal
          isOpen={isMultipleSessionFound}
          setIsOpen={setIsMultipleSessionFound}
          onEndSession={(event) => signInHandler(event, true)}
        />
      )}
    </LoginContainer>
  );
};

export default LoginForm;
